import { Stack, Group, Skeleton, Button } from '@forwardflow/ui-v2'
import { useGetMonthlyBudgets } from '@forwardflow/hooks/customHooks/useGetMonthlyBudgets'
import { useGetUserSubscription } from '@forwardflow/hooks/customHooks'
import { ChangeColdLeadsLimitModal } from '@forwardflow/modals/ChangeColdLeadsLimitModal'
import { ChangeNewLeadsLimitModal } from '@forwardflow/modals/ChangeNewLeadsLimitModal'
import { CreditRefillPolicyModal } from '@forwardflow/modals/CreditRefillPolicyModal'
import OrganizationService from '@forwardflow/services/OrganizationService'
import { useState } from 'react'
import { message } from 'antd'

interface CreditsUsedProps {
   onChangeLimitClick?: (campaignType: string, currentLimit: number) => void
}

export const CreditsUsed = ({ onChangeLimitClick }: CreditsUsedProps) => {
   const { data, isLoading, error, mutate } = useGetMonthlyBudgets()
   const { data: subscriptionData } = useGetUserSubscription()
   const [isColdLeadsModalOpen, setIsColdLeadsModalOpen] = useState(false)
   const [isNewLeadsModalOpen, setIsNewLeadsModalOpen] = useState(false)
   const [isRefillPolicyModalOpen, setIsRefillPolicyModalOpen] = useState(false)
   const [selectedCampaign, setSelectedCampaign] = useState<{
      type: string
      limit: number
   } | null>(null)
   const [isSaving, setIsSaving] = useState(false)

   if (isLoading) {
      return (
         <div className="bg-[#1e1e24] rounded-2xl p-8 border border-white/10">
            <Skeleton height={200} />
         </div>
      )
   }

   if (error) {
      return (
         <div className="bg-[#1e1e24] rounded-2xl p-8 border border-white/10">
            <p className="text-red-400">Failed to load credits data</p>
         </div>
      )
   }

   if (!data) {
      return null
   }

   // Table rows for the two campaign types
   const campaigns = [
      {
         label: 'New Leads Nurture',
         creditsUsed: data.currentMonthExpenses.newLead,
         monthlySpendLimit: data.newLeadBudget,
         key: 'newLead',
      },
      {
         label: 'Cold Leads Revival',
         creditsUsed: data.currentMonthExpenses.revive,
         monthlySpendLimit: data.reactivateLeadBudget,
         key: 'revive',
      },
   ]

   // Calculate total credits used across all campaigns
   const totalCreditsUsed = campaigns.reduce(
      (sum, campaign) => sum + (campaign.creditsUsed || 0),
      0
   )

   const handleChangeLimit = (campaignType: string, currentLimit: number) => {
      setSelectedCampaign({ type: campaignType, limit: currentLimit })

      if (campaignType === 'Cold Leads Revival') {
         setIsColdLeadsModalOpen(true)
      } else if (campaignType === 'New Leads Nurture') {
         setIsNewLeadsModalOpen(true)
      } else if (onChangeLimitClick) {
         onChangeLimitClick(campaignType, currentLimit)
      }
   }

   const handleSaveLimit = async (newLimit: number) => {
      if (!data || isSaving) return

      try {
         setIsSaving(true)

         const updatePayload =
            selectedCampaign?.type === 'Cold Leads Revival'
               ? {
                    newLeadBudget: data.newLeadBudget,
                    reactivateLeadBudget: newLimit,
                 }
               : {
                    newLeadBudget: newLimit,
                    reactivateLeadBudget: data.reactivateLeadBudget,
                 }

         await OrganizationService.updateMonthlyBudgets(updatePayload)

         // Refetch the data
         await mutate()

         message.success('Monthly budget updated successfully')

         // Close the modals
         setIsColdLeadsModalOpen(false)
         setIsNewLeadsModalOpen(false)
      } catch (err) {
         message.error('Failed to update monthly budget')
      } finally {
         setIsSaving(false)
      }
   }

   return (
      <div className="bg-[#474747]/10 rounded-xl">
         {/* Header Section */}
         <div className="px-8 pt-8 pb-6">
            <Group justify="space-between" align="center">
               <h2 className="text-white text-2xl font-medium">
                  Credits used this month
               </h2>
               <span className="text-[#c4b5fd] text-3xl font-semibold">
                  ${totalCreditsUsed.toFixed(2)}
               </span>
            </Group>
         </div>

         {/* Table Section */}
         <div className="px-8 pb-6">
            <div className="border border-white/10 rounded-xl overflow-hidden">
               {/* Table Header */}
               <div className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-4 px-6 py-4 bg-white/5 border-b border-white/10">
                  <div className="text-[#9ca3af] text-sm font-medium">
                     Campaign Type
                  </div>
                  <div className="text-[#9ca3af] text-sm font-medium">
                     Credits used
                  </div>
                  <div className="text-[#9ca3af] text-sm font-medium text-nowrap">
                     Monthly Spend Limit
                  </div>
                  <div></div>
               </div>

               {/* Table Body */}
               {campaigns.map((budget) => (
                  <div
                     key={budget.key}
                     className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-4 px-6 py-4 border-b border-white/10 last:border-b-0 hover:bg-white/[0.02] transition-colors"
                  >
                     <div className="text-white text-base">{budget.label}</div>
                     <div className="text-white text-base">
                        ${budget.creditsUsed?.toFixed(2)}
                     </div>
                     <div className="text-white text-base">
                        ${budget.monthlySpendLimit}
                     </div>
                     <div className="flex justify-end">
                        <button
                           onClick={() =>
                              handleChangeLimit(
                                 budget.label,
                                 budget.monthlySpendLimit
                              )
                           }
                           className="px-4 py-2 text-sm text-white bg-black/10 hover:bg-white/20 rounded-lg transition-colors  text-nowrap"
                        >
                           Change Limit
                        </button>
                     </div>
                  </div>
               ))}
            </div>
         </div>

         {/* Credit Balance Section */}
         <div className="px-8 pb-8">
            <div className="bg-[#2d2d35] rounded-xl px-6 py-4 border border-white/10">
               <Group justify="space-between" align="center">
                  <Stack gap="xs">
                     <p className="text-white text-lg font-medium">
                        Credit Balance:{' '}
                        <span className="text-[#c4b5fd]">
                           ${subscriptionData?.creditInfo?.remainingCredits ?? 0}
                        </span>
                     </p>
                     <p className="text-xs -mt-2 text-white/70">
                        We autofill your wallet when it hits $20
                     </p>
                  </Stack>
                  <Button
                     onClick={() => setIsRefillPolicyModalOpen(true)}
                     className=" text-primary bg-transparent border-0 hover:bg-[#7c3aed] rounded-lg transition-colors font-medium"
                  >
                     View Refill Policy
                  </Button>
               </Group>
            </div>
         </div>

         {/* Change Limit Modals */}
         <ChangeColdLeadsLimitModal
            open={isColdLeadsModalOpen}
            currentLimit={selectedCampaign?.limit || 0}
            onClose={() => setIsColdLeadsModalOpen(false)}
            onSave={handleSaveLimit}
         />
         <ChangeNewLeadsLimitModal
            open={isNewLeadsModalOpen}
            currentLimit={selectedCampaign?.limit || 0}
            onClose={() => setIsNewLeadsModalOpen(false)}
            onSave={handleSaveLimit}
         />
         <CreditRefillPolicyModal
            open={isRefillPolicyModalOpen}
            onClose={() => setIsRefillPolicyModalOpen(false)}
         />
      </div>
   )
}
